import { Component, HostListener, OnInit } from '@angular/core';
import { AppService } from '../../../services/app.service';
import { School } from '../../models/school.model';
import { User } from '../../models/user.model';
import { RecentImpersonation } from '../../models/RecentImpersonation';
import { JobFunction } from '../../models/JobFunction';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, startWith, Subject, switchMap } from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UserProfileService } from '../../../services/user-profile.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { AlertModalComponent } from '../../apps/shared/modal/alert-modal/alert-modal.component';
import { AdaptiveContainerService } from '../../apps/shared/adaptive-container/adaptive-container.service';
import { UserService } from '../../../services/user-service.service';
import { checkIsWebView } from '../../../helpers/screen-utils';
import { AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { AppsHeaderComponent } from '../../apps/apps-header/apps-header.component';
import { MatSelect } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { AppsFooterComponent } from '../../apps/apps-footer/apps-footer.component';
import { HybridStorageKeys, HybridStorageService } from '../../services/hybrid-storage.service';
import { SchoolsService } from '../../apps/shared/schools.service';
import { PostsService } from '../../apps/posts/posts.service';
import { AiChatWidgetService } from '../../apps/shared/ai/ai-chat-widget/ai-chat-widget.service';
import { MainMenuService } from '../../../services/main-menu.service';
import { SparkService } from '../../apps/spark/spark.service';
import { Staff } from '../../apps/spark/models/rewards/Staff';
import { ImpersonationLogsComponent } from '../../apps/impersonation-logs/impersonation-logs.component';
import { PageHeaderComponent } from '../../apps/shared/page-header/page-header.component';
import { GradeLevelOption, ImpersonationUserSearchRequest, ImpersonationUserSearchResult } from '../../models/ImpersonationUserSearch';
import { PaginationModel } from '../../apps/shared/pagination-model';
import { UserImageComponent } from '../../apps/shared/user-image/user-image.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-impersonate',
    standalone: true,
    templateUrl: './impersonate.component.html',
    styleUrl: './impersonate.component.scss',
    imports: [AppsHeaderComponent, MatSelect, MatFormField, MatLabel, MatPrefix, MatSuffix, MatInput, MatTabsModule, MatProgressSpinnerModule, FormsModule, MatOption, MatAutocompleteTrigger, ReactiveFormsModule, MatAutocomplete, AppsFooterComponent, AsyncPipe, DatePipe, NgTemplateOutlet, ImpersonationLogsComponent, PageHeaderComponent, UserImageComponent]
})
export class ImpersonateComponent implements OnInit {
  schoolList: School[] = [];
  userList: any[] = [];
  jobFunctionList: JobFunction[] = [];
  gradeLevelList: GradeLevelOption[] = [];
  recentImpersonations: RecentImpersonation[] = [];
  filteredOptions: Observable<any[]>;
  myControl = new FormControl({value: '', disabled: true});
  filteredSchools: Observable<School[]>;
  schoolFilterControl = new FormControl({value: '', disabled: true});

  impersonatedUser: User | undefined = undefined;
  selectedSchoolId: number;
  selectedUser: any;
  selectedUserType: string;
  selectedJobFunctionCode: string | null = null;
  selectedGradeLevelId: number | null = null;

  readonly RECENT_TAB = 0;
  readonly RESULTS_TAB = 1;
  readonly FAVORITES_TAB = 2;
  readonly GROUPS_TAB = 3;
  /** Recent is the landing tab; searching auto-switches to Results. */
  selectedResultsTabIndex: number = this.RECENT_TAB;
  /** Null until loaded; same shape as search results so the cards render identically. */
  recentResults: ImpersonationUserSearchResult[] | null = null;

  searchTextControl = new FormControl('');
  /** Null means "no active search" — before any filter is touched, or after all filters clear. */
  resultsPage: PaginationModel<ImpersonationUserSearchResult> | null = null;
  currentPage: number = 1;
  readonly resultsPageSize = 25;
  isSearching: boolean = false;
  searchError: boolean = false;
  private searchTrigger$ = new Subject<void>();

  isWebView: boolean = false;

  /** When true, the screen shows the impersonation audit logs instead of the impersonate form. */
  showLogs: boolean = false;

  /**
   * Users with the full 'Impersonate' role may impersonate any user type.
   * Users with only the 'CanImpersonateStaff' role are limited to Staff.
   */
  get canImpersonateAllUserTypes(): boolean {
    return this.userService.CurrentUserProfile?.Roles?.some(r => r.RoleName === 'Impersonate') ?? false;
  }

  /**
   * Only full 'Impersonate' users may view the audit logs.
   * Users with only 'CanImpersonateStaff' must not see the logs button.
   */
  get canViewImpersonationLogs(): boolean {
    return this.userService.CurrentUserProfile?.Roles?.some(r => r.RoleName === 'Impersonate') ?? false;
  }

  toggleLogs(): void {
    this.showLogs = !this.showLogs;
  }

  constructor(public appService: AppService,
              private adaptiveDialog: AdaptiveContainerService,
              public userProfileService: UserProfileService,
              public router: Router,
              private userService: UserService,
              private hybridStorage: HybridStorageService,
              private schoolsService: SchoolsService,
              private postsService: PostsService,
              private aiChatWidgetService: AiChatWidgetService,
              // Starting an impersonation only router.navigate()s, so the in-memory menu has to
              // be dropped explicitly — clearing storage alone leaves the BehaviorSubject stale.
              private mainMenuService: MainMenuService,
              private sparkService: SparkService) {}

  ngOnInit(): void {
    this.checkScreen();

    // Staff-only impersonators can only ever pick Staff, so pre-select it.
    if (!this.canImpersonateAllUserTypes) {
      this.selectedUserType = 'staff';
    }

    this.userProfileService.getImpersonatedUserProfile().subscribe(
      (result: User) => {
        this.impersonatedUser = result;
      }
    )

    this.appService.getCurrentSchoolList().subscribe(
      (results: School[]) => {
        this.schoolList = results;
        // enable() emits valueChanges, which refreshes filteredSchools with the loaded list
        if (results.length > 0) {
          this.schoolFilterControl.enable();
        }
      }
    )

    this.userProfileService.getJobFunctions().subscribe(
      (results: JobFunction[]) => {
        this.jobFunctionList = results;
      }
    )

    this.userProfileService.getGradeLevels().subscribe(
      (results: GradeLevelOption[]) => {
        this.gradeLevelList = results;
      }
    )

    // Load recent impersonations
    this.userProfileService.getRecentImpersonations().subscribe(
      (results: RecentImpersonation[]) => {
        this.recentImpersonations = results;
      }
    )

    // Same recents, enriched to the search-result shape for the Recent tab's cards
    this.userProfileService.getRecentImpersonationSearchResults().subscribe(
      (results: ImpersonationUserSearchResult[]) => {
        this.recentResults = results;
      }
    )

    this.filteredOptions = this.myControl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value),
      map(address => address ? this._filter(address) : this.userList.slice())
    );

    this.filteredSchools = this.schoolFilterControl.valueChanges.pipe(
      startWith(''),
      map(value => this.getSchoolName(value)),
      map(name => name ? this.schoolList.filter(s => s.SchoolName.toLowerCase().includes(name.toLowerCase())) : this.schoolList.slice())
    );

    this.searchTextControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => this.onSearchFiltersChanged());

    // Clearing the school text in the filter bar removes the school filter. Partial typing
    // emits strings too, so only a fully-cleared value counts; the enable() emission on load
    // is a no-op because no school is selected yet.
    this.schoolFilterControl.valueChanges.subscribe(value => {
      if (value === '' && this.selectedSchoolId != null) {
        this.selectedSchoolId = undefined as any;
        this.onSearchFiltersChanged();
      }
    });

    // Single search pipeline; switchMap drops in-flight responses when filters change again.
    this.searchTrigger$.pipe(
      switchMap(() => {
        const request = this.buildSearchRequest();
        if (!request) {
          this.resultsPage = null;
          return of(null);
        }
        this.isSearching = true;
        this.searchError = false;
        return this.userProfileService.searchImpersonationUsers(request).pipe(
          catchError(() => {
            this.searchError = true;
            return of(null);
          })
        );
      })
    ).subscribe(page => {
      this.isSearching = false;
      this.resultsPage = page;
    });
  }

  @HostListener('window:resize')
  checkScreen() {
    this.isWebView = checkIsWebView();
  }

  impersonateUser() {
    // Get the school name from the selected school
    const selectedSchool = this.schoolList.find(s => s.Id === this.selectedSchoolId);
    const schoolName = selectedSchool!.SchoolName;

    // First stop any existing impersonation to clear cached profiles
    this.userProfileService.clearImpersonation().subscribe(
      { next: async () => {
        // Clear storage for impersonation
        await this.hybridStorage.clearStorageForImpersonation();

        // Now proceed with the new impersonation
        this.userProfileService.impersonateUser(
          this.selectedUserType === 'staff' ? this.selectedUser.UserId : this.selectedUser.Id,
          this.selectedUser.FirstName,
          this.selectedUser.LastName,
          this.selectedUserType,
          schoolName
        ).subscribe(
          { next: async (result: User) => {
            await this.userService.setCurrentUserProfile(result);
            await this.hybridStorage.setItem(HybridStorageKeys.MainMenu_UserFirstName, this.userService.CurrentUserProfile?.FirstName);

            // Clear in-memory caches and initialize directory data for the impersonated user
            this.schoolsService.clearCaches();
            this.postsService.clearCaches();
            this.aiChatWidgetService.resetAll();
            this.mainMenuService.clearCaches();
            this.schoolsService.initializeDirectoryData();

            this.router.navigate(['mainmenu']);
          },
            error: (err: HttpErrorResponse) => {
              console.error('Error during impersonation', err);
              AlertModalComponent.open(this.adaptiveDialog, 'Error', 'There was a problem setting the impersonation.');
              this.stopImpersonatingUser();
            }
          }
        )
      },
        error: async () => {
          // Even if clearing fails, proceed with impersonation
          await this.hybridStorage.clearStorageForImpersonation();

          this.userProfileService.impersonateUser(
            this.selectedUserType === 'staff' ? this.selectedUser.UserId : this.selectedUser.Id,
            this.selectedUser.FirstName,
            this.selectedUser.LastName,
            this.selectedUserType,
            schoolName
          ).subscribe(
            { next: async (result: User) => {
              await this.userService.setCurrentUserProfile(result);
              await this.hybridStorage.setItem(HybridStorageKeys.MainMenu_UserFirstName, this.userService.CurrentUserProfile?.FirstName);

              // Clear in-memory caches and initialize directory data for the impersonated user
              this.schoolsService.clearCaches();
              this.postsService.clearCaches();
              this.aiChatWidgetService.resetAll();
              this.mainMenuService.clearCaches();
              this.schoolsService.initializeDirectoryData();

              this.router.navigate(['mainmenu']);
            },
              error: (err: HttpErrorResponse) => {
                console.error('Error during impersonation', err);
                AlertModalComponent.open(this.adaptiveDialog, 'Error', 'There was a problem setting the impersonation.');
                this.stopImpersonatingUser();
              }
            }
          )
        }
      }
    )
  }

  onSchoolSelected(_: any) {
    this.getUsers();
    this.myControl.reset();
    this.onSearchFiltersChanged();
  }

  getSchoolName(school: any): string {
    return typeof school === 'string' ? school : school?.SchoolName ?? '';
  }

  onSchoolFilterSelected(school: School) {
    this.selectedSchoolId = school.Id;
    this.onSchoolSelected(school.Id);
  }

  onUserSelected(user: any) {
    this.selectedUser = user;
  }

  /** The third filter is Grade Level for students/guardians, Job Title otherwise. */
  get showGradeLevelFilter(): boolean {
    return this.selectedUserType === 'student' || this.selectedUserType === 'guardian';
  }

  onUserTypeSelected(userType: any) {
    // Each filter only applies to some user types; drop the one that no longer
    // applies so a stale value can't zero out results.
    if (userType !== 'staff') {
      this.selectedJobFunctionCode = null;
    }
    if (userType !== 'student' && userType !== 'guardian') {
      this.selectedGradeLevelId = null;
    }
    this.getUsers();
    this.myControl.reset();
    this.onSearchFiltersChanged();
  }

  onJobFunctionSelected() {
    this.onSearchFiltersChanged();
  }

  onGradeLevelSelected() {
    this.onSearchFiltersChanged();
  }

  // Clear buttons sit in the form-field suffix; stopPropagation keeps the click
  // from reaching the field container, which would open the select panel.
  clearSchoolFilter(event: Event) {
    event.stopPropagation();
    // Emits '' through valueChanges, which drops the school filter and re-searches
    this.schoolFilterControl.setValue('');
  }

  clearUserType(event: Event) {
    event.stopPropagation();
    this.selectedUserType = null as any;
    this.onUserTypeSelected(null);
  }

  clearJobFunction(event: Event) {
    event.stopPropagation();
    this.selectedJobFunctionCode = null;
    this.onSearchFiltersChanged();
  }

  clearGradeLevel(event: Event) {
    event.stopPropagation();
    this.selectedGradeLevelId = null;
    this.onSearchFiltersChanged();
  }

  /**
   * True when the user has actively set any search input. The programmatic staff
   * preset for limited impersonators doesn't count — they never chose it.
   */
  private hasUserSearchCriteria(): boolean {
    const searchText = (this.searchTextControl.value ?? '').trim();
    const typeIsUserChosen = !!this.selectedUserType && this.canImpersonateAllUserTypes;
    return !!searchText || !!this.selectedSchoolId || !!this.selectedJobFunctionCode
      || this.selectedGradeLevelId != null || typeIsUserChosen;
  }

  private onSearchFiltersChanged() {
    this.currentPage = 1;
    // Clearing every input slides back to Recent instead of an empty Results tab
    this.selectedResultsTabIndex = this.hasUserSearchCriteria() ? this.RESULTS_TAB : this.RECENT_TAB;
    this.searchTrigger$.next();
  }

  goToPage(page: number) {
    if (page < 1 || page === this.currentPage || (this.resultsPage && page > this.resultsPage.TotalPages)) {
      return;
    }
    this.currentPage = page;
    this.searchTrigger$.next();
  }

  /** Window of up to 5 page numbers centered on the current page. */
  get pageNumbers(): number[] {
    const total = this.resultsPage?.TotalPages ?? 0;
    const count = Math.min(5, total);
    const start = Math.max(1, Math.min(this.currentPage - 2, total - count + 1));
    return Array.from({ length: count }, (_, i) => start + i);
  }

  private buildSearchRequest(): ImpersonationUserSearchRequest | null {
    if (!this.hasUserSearchCriteria()) {
      return null;
    }
    const searchText = (this.searchTextControl.value ?? '').trim();
    return {
      SearchText: searchText || null,
      SchoolId: this.selectedSchoolId ?? null,
      UserType: this.selectedUserType ?? null,
      JobFunctionCode: this.selectedJobFunctionCode,
      GradeLevelId: this.selectedGradeLevelId,
      Page: this.currentPage,
      PageSize: this.resultsPageSize
    };
  }

  getTypeIdLabel(userType: string): string {
    switch (userType) {
      case 'staff': return 'Staff ID';
      case 'student': return 'Student ID';
      default: return 'Guardian ID';
    }
  }

  impersonateFromSearchResult(result: ImpersonationUserSearchResult) {
    this.startImpersonation(result.UserId, result.FirstName, result.LastName, result.UserType, result.SchoolName ?? '');
  }

  stopImpersonatingUser() {
    this.userProfileService.clearImpersonation().subscribe(
      { next: async (result: User) => {
        // Clear storage for impersonation
        await this.hybridStorage.clearStorageForImpersonation();

        await this.userService.setCurrentUserProfile(result);
        await this.hybridStorage.setItem(HybridStorageKeys.MainMenu_UserFirstName, this.userService.CurrentUserProfile?.FirstName)

        // Clear in-memory caches and initialize directory data for the original user
        this.schoolsService.clearCaches();
        this.postsService.clearCaches();
        this.aiChatWidgetService.resetAll();
        this.mainMenuService.clearCaches();
        this.schoolsService.initializeDirectoryData();

        this.router.navigate(['mainmenu']);
      },
        error: (err: HttpErrorResponse) => {
          console.error('Error stopping impersonation', err);
          // shouldn't show error to user
        }
      }
    )
  }

  private _filter(value: string): any[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value;

    return this.userList.filter(option => (option.FirstName + " " + option.LastName).toLowerCase().includes(filterValue));
  }

  getUserName(user: User): string {
    return user && user.FirstName ? user.FirstName + " " + user.LastName : "";
  }

  getUsers() {
    if(!this.selectedUserType || !this.selectedSchoolId) {
      this.userList = [];
      this.myControl.disable();
      return;
    }

    if(this.selectedUserType === 'staff'){
      this.sparkService.getStaffBySchoolId(this.selectedSchoolId).subscribe(
        (results: Staff[]) => {
          this.userList = results;
          if (results.length > 0) {
            this.myControl.enable();
          } else {
            this.myControl.disable();
          }
        }
      )
    } else if (this.selectedUserType === 'guardian') {
      this.userProfileService.getGuardiansBySchoolId(this.selectedSchoolId).subscribe(
        (results: User[]) => {
          this.userList = results;
          if (results.length > 0) {
            this.myControl.enable();
          } else {
            this.myControl.disable();
          }
        }
      )
    } else {
      this.userProfileService.getStudentsBySchoolId(this.selectedSchoolId).subscribe(
        (results: User[]) => {
          this.userList = results;
          if (results.length > 0) {
            this.myControl.enable();
          } else {
            this.myControl.disable();
          }
        }
      )
    }
  }

  impersonateFromRecentList(recentImpersonation: RecentImpersonation) {
    this.startImpersonation(
      recentImpersonation.ImpersonatedUserId,
      recentImpersonation.ImpersonatedUserFirstName,
      recentImpersonation.ImpersonatedUserLastName,
      recentImpersonation.ImpersonatedUserType,
      recentImpersonation.SchoolName
    );
  }

  private startImpersonation(userId: number, firstName: string, lastName: string, userType: string, schoolName: string) {
    // First stop any existing impersonation to clear cached profiles
    this.userProfileService.clearImpersonation().subscribe(
      { next: async () => {
        // Clear storage for impersonation
        await this.hybridStorage.clearStorageForImpersonation();

        // Now proceed with the new impersonation
        this.userProfileService.impersonateUser(
          userId,
          firstName,
          lastName,
          userType,
          schoolName
        ).subscribe(
          { next: async (result: User) => {
            if(result != null) {
              await this.userService.setCurrentUserProfile(result);
              await this.hybridStorage.setItem(HybridStorageKeys.MainMenu_UserFirstName, this.userService.CurrentUserProfile?.FirstName);

              // Clear in-memory caches and initialize directory data for the impersonated user
              this.schoolsService.clearCaches();
              this.postsService.clearCaches();
              this.aiChatWidgetService.resetAll();
              this.mainMenuService.clearCaches();
              this.schoolsService.initializeDirectoryData();

              this.router.navigate(['mainmenu']);
            } else {
              AlertModalComponent.open(this.adaptiveDialog, 'Error', 'There was a problem setting the impersonation.');
              this.stopImpersonatingUser();
            }
          },
            error: (err: HttpErrorResponse) => {
              console.error('Error during impersonation', err);
              AlertModalComponent.open(this.adaptiveDialog, 'Error', 'There was a problem setting the impersonation.');
              this.stopImpersonatingUser();
            }
          }
        )
      },
        error: async () => {
          // Even if clearing fails, proceed with impersonation
          await this.hybridStorage.clearStorageForImpersonation();

          this.userProfileService.impersonateUser(
            userId,
            firstName,
            lastName,
            userType,
            schoolName
          ).subscribe(
            { next: async (result: User) => {
              await this.userService.setCurrentUserProfile(result);
              await this.hybridStorage.setItem(HybridStorageKeys.MainMenu_UserFirstName, this.userService.CurrentUserProfile?.FirstName);

              // Clear in-memory caches and initialize directory data for the impersonated user
              this.schoolsService.clearCaches();
              this.postsService.clearCaches();
              this.aiChatWidgetService.resetAll();
              this.mainMenuService.clearCaches();
              this.schoolsService.initializeDirectoryData();

              this.router.navigate(['mainmenu']);
            },
              error: (err: HttpErrorResponse) => {
                console.error('Error during impersonation', err);
                AlertModalComponent.open(this.adaptiveDialog, 'Error', 'There was a problem setting the impersonation.');
                this.stopImpersonatingUser();
              }
            }
          )
        }
      }
    )
  }

  deleteRecentImpersonation(event: Event, impersonationId: number) {
    // Stop event propagation to prevent triggering the parent click event
    event.stopPropagation();

    this.userProfileService.deleteRecentImpersonation(impersonationId).subscribe({
      next: () => {
        // Remove the deleted item from the local list
        this.recentImpersonations = this.recentImpersonations.filter(
          r => r.Id !== impersonationId
        );
      },
      error: (err: HttpErrorResponse) => {
        console.error('Error deleting recent impersonation', err);
        AlertModalComponent.open(this.adaptiveDialog, 'Error', 'There was a problem deleting the impersonation.');
      }
    });
  }

}
